import React, { useState, useRef, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useTranslation } from 'react-i18next';
import { apiCall } from '../../api';
import { setI18nLanguage } from '../../i18n/config';
import i18n from '../../i18n/config';

// TODO(tech-debt): hardcoded role -> id mapping. These ids follow the order
// seeded by rbac migrations 0003/0006 (admin, treasury, governing_body,
// normal_member, donor, senior_accounting_clerk, junior_accounting_clerk).
// Prefer replacing this with a fetched roles list (same pattern as Bank
// Account in AddExpensePage.jsx) once a GET /api/organizations/{id}/roles/
// -style endpoint exists.
const ROLE_MAP = {
  'Organisation Admin': 1,
  Treasurer: 2,
  'Governing Body': 3,
  'Normal Member': 4,
  Donor: 5,
  'Senior Clerk': 6,
  'Junior Clerk': 7,
};

// Pull the real backend message out of whatever apiCall threw.
const extractErrorMessage = (err) => {
  if (!err) return 'Unable to add user.';
  if (typeof err.detail === 'string' && err.detail) return err.detail;
  if (typeof err.message === 'string' && err.message) return err.message;
  if (typeof err === 'object') {
    for (const value of Object.values(err)) {
      if (typeof value === 'string' && value) return value;
      if (Array.isArray(value) && value.length && typeof value[0] === 'string') {
        return value[0];
      }
    }
  }
  return 'Unable to add user.';
};

const AddUser = ({ ngo, onBack, onHome, onEditProfile }) => {
  const { user, getAccessTokenSilently, logout } = useAuth0();
  const { t } = useTranslation('addUser');
  const { t: tc } = useTranslation('common');

  const [email, setEmail] = useState('');
  const [accessLevel, setAccessLevel] = useState('');
  const [errors, setErrors] = useState({});
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [serverError, setServerError] = useState('');

  const [profileOpen, setProfileOpen] = useState(false);
  const profileRef = useRef(null);

  // ---- Language menu state ----
  const [langMenuOpen, setLangMenuOpen] = useState(false);
  const langMenuRef = useRef(null);

  useEffect(() => {
    if (!profileOpen) return;
    const handleClickOutside = (e) => {
      if (profileRef.current && !profileRef.current.contains(e.target)) {
        setProfileOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [profileOpen]);

  // Close the language menu on outside click.
  useEffect(() => {
    if (!langMenuOpen) return;
    const handleClickOutside = (e) => {
      if (langMenuRef.current && !langMenuRef.current.contains(e.target)) {
        setLangMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [langMenuOpen]);

  const validate = () => {
    const newErrors = {};
    if (!email || !/\S+@\S+\.\S+/.test(email))
      newErrors.email = t('validation.emailRequired');
    if (!accessLevel) newErrors.accessLevel = t('validation.accessLevelRequired');
    return newErrors;
  };

  const handleSubmit = async () => {
    const newErrors = validate();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    if (!ngo?.id) {
      setServerError(t('noOrgSelected'));
      return;
    }

    setErrors({});
    setServerError('');
    setLoading(true);

    try {
      await apiCall(
        `/api/organizations/${ngo.id}/members/`,
        {
          method: 'POST',
          body: JSON.stringify({
            email,
            role_id: ROLE_MAP[accessLevel],
          }),
        },
        getAccessTokenSilently
      );

      setSuccess(true);
    } catch (err) {
      setServerError(extractErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const handleAddAnother = () => {
    setSuccess(false);
    setEmail('');
    setAccessLevel('');
  };

  const handleLogout = () => {
    setProfileOpen(false);
    logout({ logoutParams: { returnTo: window.location.origin } });
  };

  const handleEditProfile = () => {
    setProfileOpen(false);
    if (onEditProfile) onEditProfile();
  };
  const inputCls =
    'w-full bg-gray-100 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-400 border-0';

  // Success state is a full-page replacement, matching CreateOrganization.jsx.
  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="bg-white rounded-2xl border border-gray-200 p-10 text-center max-w-sm w-full mx-4">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
              <path
                d="M5 13l4 4L19 7"
                stroke="#16a34a"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
          <p className="text-base font-medium text-gray-900 mb-6">{t('successTitle')}</p>
          <button
            onClick={handleAddAnother}
            className="w-full bg-blue-700 hover:bg-blue-800 text-white rounded-lg py-2.5 text-sm font-medium transition"
          >
            {t('done')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Top bar — DESIGN.md §2 (Pattern A: py-4 / gap-3 / flex-1 spacer) */}
      <div className="bg-white border-b border-gray-200 px-4 py-4 flex items-center gap-3">
        {onHome && (
          <button
            onClick={onHome}
            aria-label="Home"
            title="Home"
            className="text-blue-600 hover:text-blue-700 flex-shrink-0"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" className="fill-current">
              <path d="M12 3.2 3 10v10h6v-6h6v6h6V10l-9-6.8Z" />
            </svg>
          </button>
        )}

        <button onClick={onBack} aria-label="Back" className="text-blue-600 hover:text-blue-700">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
            <path
              d="M19 12H5M5 12l7-7M5 12l7 7"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
        </button>

        {/* Top-bar label */}
        <span className="text-sm font-medium text-gray-900 flex-1">{t('title')}</span>

        <div className="flex-1" />

        {/* ---- LANGUAGE TOGGLE ---- */}
        <div ref={langMenuRef} className="relative">
          <button
            onClick={() => setLangMenuOpen((open) => !open)}
            aria-label="Language menu"
            aria-expanded={langMenuOpen}
            className="text-xs font-medium text-gray-600 border border-gray-200 rounded-lg px-2.5 py-1.5 hover:bg-gray-50 transition"
          >
            {i18n.language === 'ml' ? 'മലയാളം' : 'EN'}
          </button>
          {langMenuOpen && (
            <div className="absolute right-0 top-10 w-32 bg-white rounded-xl shadow-lg border border-gray-100 p-2 z-50">
              <button
                onClick={() => {
                  const lang = 'en';
                  setI18nLanguage(lang);
                  localStorage.setItem('zbookkeeping_language', lang);
                  setLangMenuOpen(false);
                  // Persist to backend (fire-and-forget)
                  getAccessTokenSilently({
                    authorizationParams: { audience: process.env.REACT_APP_AUTH0_AUDIENCE },
                  }).then(token => {
                    fetch(`${window.location.origin}/api/accounts/profile/update/`, {
                      method: 'PATCH',
                      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                      body: JSON.stringify({ preferred_language: lang }),
                    }).catch(() => {});
                  }).catch(() => {});
                }}
                className={`w-full text-left rounded-lg px-3 py-2 text-sm transition ${
                  i18n.language === 'en' ? 'bg-blue-50 text-blue-700 font-medium' : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                English
              </button>
              <button
                onClick={() => {
                  const lang = 'ml';
                  setI18nLanguage(lang);
                  localStorage.setItem('zbookkeeping_language', lang);
                  setLangMenuOpen(false);
                  // Persist to backend (fire-and-forget)
                  getAccessTokenSilently({
                    authorizationParams: { audience: process.env.REACT_APP_AUTH0_AUDIENCE },
                  }).then(token => {
                    fetch(`${window.location.origin}/api/accounts/profile/update/`, {
                      method: 'PATCH',
                      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                      body: JSON.stringify({ preferred_language: lang }),
                    }).catch(() => {});
                  }).catch(() => {});
                }}
                className={`w-full text-left rounded-lg px-3 py-2 text-sm transition ${
                  i18n.language === 'ml' ? 'bg-blue-50 text-blue-700 font-medium' : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                മലയാളം
              </button>
            </div>
          )}
        </div>

        {/* ---- USER AVATAR + PROFILE DROPDOWN ---- */}
        <div ref={profileRef} className="relative">
          <button
            onClick={() => setProfileOpen((open) => !open)}
            aria-label="Account menu"
            aria-expanded={profileOpen}
            className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center text-white text-xs font-medium"
          >
            {user?.name?.charAt(0).toUpperCase() || 'U'}
          </button>

          {profileOpen && (
            <div className="absolute right-0 top-11 w-64 bg-white rounded-xl shadow-lg border border-gray-100 p-4 z-50">
              <p className="text-sm text-gray-900 truncate">{user?.email}</p>
              <p className="text-sm text-gray-400 truncate mb-4">{user?.email}</p>

              <button
                onClick={handleEditProfile}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white rounded-lg py-2.5 text-sm font-medium transition mb-2"
              >
                {tc('editProfile')}
              </button>
              <button
                onClick={handleLogout}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white rounded-lg py-2.5 text-sm font-medium transition"
              >
                {tc('logout')}
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="max-w-sm mx-auto px-4 py-8">
        <h1 className="text-center text-base font-medium text-blue-600 mb-6 uppercase tracking-wider">
          {t('title')}
        </h1>

        <div className="mb-4">
          <p className="text-xs text-gray-500 uppercase tracking-widest mb-1.5">
            {t('emailLabel')} <span className="text-red-500">*</span>
          </p>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className={inputCls}
          />
          {errors.email && <p className="text-xs text-red-500 mt-1">{errors.email}</p>}
        </div>

        <div className="mb-6">
          <p className="text-xs text-gray-500 uppercase tracking-widest mb-1.5">
            {t('accessLevel')} <span className="text-red-500">*</span>
          </p>
          <select
            value={accessLevel}
            onChange={(e) => setAccessLevel(e.target.value)}
            className={inputCls}
          >
            <option value="">{t('selectAccessLevel')}</option>
            <option value="Organisation Admin">{t('organisationAdmin')}</option>
            <option value="Treasurer">{t('treasurer')}</option>
            <option value="Senior Clerk">{t('seniorClerk')}</option>
            <option value="Junior Clerk">{t('juniorClerk')}</option>
            <option value="Governing Body">{t('governingBody')}</option>
            <option value="Normal Member">{t('normalMember')}</option>
            <option value="Donor">{t('donor')}</option>
          </select>
          {errors.accessLevel && (
            <p className="text-xs text-red-500 mt-1">{errors.accessLevel}</p>
          )}
        </div>

        {serverError && (
          <div className="mb-4 bg-red-50 border border-red-200 rounded-lg px-3 py-2.5">
            <p className="text-xs text-red-600">{serverError}</p>
          </div>
        )}

        <button
          onClick={handleSubmit}
          disabled={loading}
          className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300 disabled:cursor-not-allowed text-white rounded-lg px-4 py-3 text-sm font-medium transition"
        >
          {loading ? t('adding') : t('add')}
        </button>
      </div>
    </div>
  );
};

export default AddUser;